// PATH: src/api/school.ts — accès à l'API /api/v1/school du backend tc_tilmischool_be
import { useMemo } from "react";
import type { AxiosInstance } from "axios";
import { useApiClient } from "./apiClients";
import type {
  AccountInput,
  CertificateData,
  DisciplinaryRecord,
  Enrollment,
  GuardianInput,
  ImportPreview,
  ImportResult,
  Level,
  Relation,
  SchoolClass,
  SchoolYear,
  Student,
  StudentDocument,
  StudentListResponse,
  StudentProfile,
} from "../models/school";

const BASE = "/api/v1/school";

export interface StudentListParams {
  q?: string;
  status?: string;
  school_year_id?: number;
  class_id?: number;
  page?: number;
  page_size?: number;
}

/**
 * Un dossier élève sans tuteur, inscription ni pièce jointe est le cas normal
 * d'une inscription qui vient d'être créée. Go sérialise une liste vide en
 * `null`, alors que le type les déclare comme des tableaux : on rétablit le
 * contrat ici, à la frontière, pour que les pages puissent s'y fier.
 *
 * `discipline` est laissée telle quelle : son type `| null` porte une
 * information — « omise car compte non-direction » — qu'un `[]` effacerait.
 */
const normalizeProfile = (p: StudentProfile): StudentProfile => ({
  ...p,
  guardians: p.guardians ?? [],
  enrollments: p.enrollments ?? [],
  documents: p.documents ?? [],
});

// buildSchoolApi expose toutes les opérations de la Phase 1 sur un client axios donné.
const buildSchoolApi = (http: AxiosInstance) => ({
  // Référentiels
  listYears: () => http.get<SchoolYear[]>(`${BASE}/school-years`).then((r) => r.data ?? []),
  createYear: (y: Partial<SchoolYear>) => http.post<SchoolYear>(`${BASE}/school-years`, y).then((r) => r.data),
  updateYear: (id: number, y: Partial<SchoolYear>) => http.put(`${BASE}/school-years/${id}`, y),
  deleteYear: (id: number) => http.delete(`${BASE}/school-years/${id}`),
  reenroll: (fromYearId: number, targetYearId: number) =>
    http
      .post<{ created: number }>(`${BASE}/school-years/${fromYearId}/reenroll`, { target_year_id: targetYearId })
      .then((r) => r.data),

  listLevels: () => http.get<Level[]>(`${BASE}/levels`).then((r) => r.data ?? []),
  createLevel: (l: Partial<Level>) => http.post<Level>(`${BASE}/levels`, l).then((r) => r.data),
  updateLevel: (id: number, l: Partial<Level>) => http.put(`${BASE}/levels/${id}`, l),
  deleteLevel: (id: number) => http.delete(`${BASE}/levels/${id}`),

  listClasses: (schoolYearId?: number) =>
    http
      .get<SchoolClass[]>(`${BASE}/classes`, { params: { school_year_id: schoolYearId } })
      .then((r) => r.data ?? []),
  createClass: (c: Partial<SchoolClass>) => http.post<SchoolClass>(`${BASE}/classes`, c).then((r) => r.data),
  updateClass: (id: number, c: Partial<SchoolClass>) => http.put(`${BASE}/classes/${id}`, c),
  deleteClass: (id: number) => http.delete(`${BASE}/classes/${id}`),

  // Élèves
  listStudents: (params: StudentListParams) =>
    http.get<StudentListResponse>(`${BASE}/students`, { params }).then((r) => r.data),
  createStudent: (student: Partial<Student> & { guardians?: GuardianInput[]; account?: AccountInput }) =>
    http.post<Student>(`${BASE}/students`, student).then((r) => r.data),
  getStudent: (id: number) =>
    http.get<StudentProfile>(`${BASE}/students/${id}`).then((r) => normalizeProfile(r.data)),
  updateStudent: (id: number, s: Partial<Student>) => http.put(`${BASE}/students/${id}`, s),
  // Relaie l'image vers tc_upload via le backend (seul point authentifié) et
  // retourne l'URL publique à ranger dans photo_url.
  uploadPhoto: (file: File) => {
    const fd = new FormData();
    fd.append("file", file);
    return http.post<{ url: string }>(`${BASE}/uploads/photo`, fd).then((r) => r.data.url);
  },
  // Pièce jointe (PDF ou image scannée). Même relais authentifié que la photo ;
  // l'URL retournée alimente file_url d'une StudentDocument.
  uploadDocument: (file: File) => {
    const fd = new FormData();
    fd.append("file", file);
    return http.post<{ url: string }>(`${BASE}/uploads/document`, fd).then((r) => r.data.url);
  },
  // Import d'élèves (Excel/CSV) — réservé à la direction côté backend.
  // Aucun état n'est conservé entre l'aperçu et l'exécution : le fichier est
  // renvoyé avec la correspondance confirmée, et tout est revalidé.
  importPreview: (file: File, mapping?: Record<string, number>) => {
    const fd = new FormData();
    fd.append("file", file);
    if (mapping) fd.append("mapping", JSON.stringify(mapping));
    return http.post<ImportPreview>(`${BASE}/students-import/preview`, fd).then((r) => r.data);
  },
  importStudents: (file: File, mapping: Record<string, number>, createAccounts: boolean) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("mapping", JSON.stringify(mapping));
    fd.append("create_accounts", createAccounts ? "true" : "false");
    // Un compte central par élève = un aller-retour tc_identity + un hachage
    // bcrypt : le délai par défaut (30 s) est très en deçà pour un fichier
    // d'établissement. Le backend s'accorde 10 minutes, le client aussi.
    return http
      .post<ImportResult>(`${BASE}/students-import`, fd, { timeout: 10 * 60 * 1000 })
      .then((r) => r.data);
  },
  importTemplate: () =>
    http.get(`${BASE}/students-import/template`, { responseType: "blob" }).then((r) => r.data as Blob),

  archiveStudent: (id: number) => http.post(`${BASE}/students/${id}/archive`),
  restoreStudent: (id: number) => http.post(`${BASE}/students/${id}/restore`),

  // Tuteurs
  addGuardian: (studentId: number, g: GuardianInput) => http.post(`${BASE}/students/${studentId}/guardians`, g),
  updateGuardian: (id: number, g: Partial<GuardianInput>) => http.put(`${BASE}/guardians/${id}`, g),
  // Relation et contact principal portent sur le LIEN élève↔tuteur, pas sur le
  // tuteur : updateGuardian n'écrit que les colonnes de school_guardians.
  setGuardianLink: (studentId: number, guardianId: number, v: { relation: Relation; is_primary?: boolean }) =>
    http.put(`${BASE}/students/${studentId}/guardians/${guardianId}`, v),
  unlinkGuardian: (studentId: number, guardianId: number) =>
    http.delete(`${BASE}/students/${studentId}/guardians/${guardianId}`),

  // Pièces jointes
  addDocument: (studentId: number, d: Partial<StudentDocument>) =>
    http.post(`${BASE}/students/${studentId}/documents`, d),
  deleteDocument: (id: number) => http.delete(`${BASE}/documents/${id}`),

  // Inscriptions
  createEnrollment: (e: Partial<Enrollment>) => http.post<Enrollment>(`${BASE}/enrollments`, e).then((r) => r.data),
  listEnrollments: (schoolYearId: number, classId?: number) =>
    http
      .get<Enrollment[]>(`${BASE}/enrollments`, { params: { school_year_id: schoolYearId, class_id: classId } })
      .then((r) => r.data ?? []),
  validateEnrollment: (id: number) => http.post(`${BASE}/enrollments/${id}/validate`),
  cancelEnrollment: (id: number) => http.post(`${BASE}/enrollments/${id}/cancel`),
  setEnrollmentClass: (id: number, classId: number | null) =>
    http.put(`${BASE}/enrollments/${id}/class`, { class_id: classId }),
  // periodId : uniquement pour RECU_PAIEMENT — le backend l'ignore sinon.
  getCertificate: (enrollmentId: number, docType: CertificateData["doc_type"], periodId?: number) =>
    http
      .get<CertificateData>(`${BASE}/enrollments/${enrollmentId}/certificate`, {
        params: { doc_type: docType, period_id: periodId },
      })
      .then((r) => r.data),

  // Discipline
  listDiscipline: (studentId: number) =>
    http.get<DisciplinaryRecord[]>(`${BASE}/students/${studentId}/discipline`).then((r) => r.data ?? []),
  createDiscipline: (studentId: number, d: Partial<DisciplinaryRecord>) =>
    http.post(`${BASE}/students/${studentId}/discipline`, d),
  deleteDiscipline: (id: number) => http.delete(`${BASE}/discipline/${id}`),
});

export type SchoolApi = ReturnType<typeof buildSchoolApi>;

export const useSchoolApi = (): SchoolApi => {
  const http = useApiClient("tilmischool");
  return useMemo(() => buildSchoolApi(http), [http]);
};
